import { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import Iconify from "src/components/iconify";

export interface BreakdownSegment {
    value: number;
    color: string;
}

export interface BreakdownRow {
    label: string;
    value: ReactNode;
    color: string;
    /** Iconify icon instead of the color marker (presence breakdown). */
    icon?: string;
}

const TEXT_SX = {
    fontFamily: '"Public Sans"',
    fontSize: '14px',
    fontStyle: 'normal',
    lineHeight: '22px',
};

/**
 * Layout of the Non Brand grid tooltips (Figma): title (+ total), optional split bar, one row per part.
 * Same look as the Brand presence breakdown tooltip.
 */
export default function BreakdownTooltip({
    title,
    total,
    segments,
    rows,
}: {
    title: string;
    total?: string;
    segments?: BreakdownSegment[];
    rows: BreakdownRow[];
}) {
    const visibleSegments = (segments ?? []).filter((segment) => segment.value > 0);
    return (
        <Stack sx={{ width: 320, boxSizing: 'border-box', backgroundColor: '#FFFFFF', borderRadius: '12px' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: '12px', px: '16px' }}>
                <Typography sx={{ ...TEXT_SX, color: '#333C47', fontWeight: 500, lineHeight: '100%' }}>{title}</Typography>
                {total !== undefined && (
                    <Typography sx={{ ...TEXT_SX, color: '#637381', fontWeight: 500, lineHeight: '100%' }}>{total}</Typography>
                )}
            </Stack>
            <Stack sx={{ px: '16px', pt: '12px', pb: '12px' }}>
                {segments && (
                    <Box
                        aria-label="tooltip-split-bar"
                        sx={{ display: 'flex', gap: '2px', width: '100%', height: '8px', my: '10px', borderRadius: '2px', overflow: 'hidden' }}
                    >
                        {visibleSegments.map((segment, index) => (
                            <Box key={index} sx={{ flex: segment.value, minWidth: 2, height: '100%', borderRadius: '2px', backgroundColor: segment.color }} />
                        ))}
                    </Box>
                )}
                {rows.map((row) => (
                    <Stack key={row.label} data-testid="tooltip-row" direction="row" alignItems="center" sx={{ height: 30 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                            {row.icon ? (
                                <Iconify icon={row.icon} sx={{ width: 14, height: 14, color: row.color, flexShrink: 0 }} />
                            ) : (
                                <Box sx={{ width: 4, height: 20, borderRadius: '16px', backgroundColor: row.color, flexShrink: 0 }} />
                            )}
                            <Typography sx={{ ...TEXT_SX, color: '#333C47', fontWeight: 400 }}>{row.label}</Typography>
                        </Stack>
                        <Typography
                            component="div"
                            sx={{ ...TEXT_SX, color: '#637381', fontWeight: 500, fontVariantNumeric: 'lining-nums tabular-nums', flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                            {row.value}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
}
